/**
 * Safe platform detection utility for iOS Safari stability
 * Wraps window.matchMedia() calls in try-catch to prevent crashes
 */

export interface PlatformInfo {
  isIOS: boolean;
  isIOSPWA: boolean;
  iosVersion: number | undefined;
  isAndroid: boolean;
  isSafari: boolean;
  isStandalone: boolean;
}

const DEFAULT_PLATFORM: PlatformInfo = {
  isIOS: false,
  isIOSPWA: false,
  iosVersion: undefined,
  isAndroid: false,
  isSafari: false,
  isStandalone: false
};

/**
 * Safely detect platform information without crashing iOS Safari
 * MUST be called only after component mount (inside useEffect)
 */
export const detectPlatformSafe = (): PlatformInfo => {
  // Server-side safety check
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return DEFAULT_PLATFORM;
  }

  try {
    const userAgent = (navigator.userAgent || '').toLowerCase();
    
    // iOS detection
    const isIPhone = /iphone/.test(userAgent);
    const isIPad = /ipad/.test(userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isIPod = /ipod/.test(userAgent);
    const isIOS = isIPhone || isIPad || isIPod;
    
    // Android detection
    const isAndroid = /android/.test(userAgent);
    
    // Safari detection
    const isSafari = /safari/.test(userAgent) && 
      !/chrome/.test(userAgent) && 
      !/crios/.test(userAgent);
    
    // Extract iOS version
    let iosVersion: number | undefined;
    const versionMatch = userAgent.match(/os (\d+)_/);
    if (versionMatch) {
      iosVersion = parseInt(versionMatch[1], 10);
    }
    
    // SAFE standalone detection - wrapped in try-catch for iOS Safari
    let isStandalone = false;
    
    // First try legacy standalone property (more reliable on iOS)
    try {
      isStandalone = (navigator as any).standalone === true;
    } catch {
      // Ignore
    }
    
    // Then try matchMedia (can crash on iOS Safari in some states)
    if (!isStandalone) {
      try {
        const mq = window.matchMedia?.('(display-mode: standalone)');
        if (mq?.matches) {
          isStandalone = true;
        }
      } catch {
        // matchMedia failed - this is the crash we're preventing
        console.warn('matchMedia failed, falling back to legacy detection');
      }
    }
    
    const isIOSPWA = isIOS && isStandalone;
    
    return {
      isIOS,
      isIOSPWA,
      iosVersion,
      isAndroid,
      isSafari,
      isStandalone
    };
  } catch (error) {
    console.warn('Platform detection failed:', error);
    return DEFAULT_PLATFORM;
  }
};

/**
 * Quick iOS check that's safe to call anywhere
 */
export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    return /iphone|ipad|ipod/.test(ua) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  } catch {
    return false;
  }
};

/**
 * Check if running as PWA - safe version
 */
export const isRunningAsPWA = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    // Legacy iOS check first
    if ((navigator as any).standalone === true) {
      return true;
    }
    
    // matchMedia check with protection
    try {
      return window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
    } catch {
      return false;
    }
  } catch {
    return false;
  }
};
